/**
 * Carbdial MVP maths -- a TypeScript port of `docs/handover/reference/calc.js`,
 * which is itself a direct port of the client's Rad / Lauf spreadsheets. The
 * cell references in the comments point at the source workbook.
 *
 * These formulas are ground truth. Do not "improve" them: a changed number here
 * is a changed race plan. If a value looks wrong, check the workbook first.
 */

export type Discipline = "swim" | "bike" | "run";
export type LineKind = "drink" | "gel" | "bar";

/** Fuelling constants (workbook, Rad sheet). */
export const K = {
  kgMin: 1.0,
  kgMax: 1.3,
  fluidMin: 500,
  fluidMax: 800,
  mlPerFourSips: 120,
} as const;

export interface Product {
  id: string;
  name: string;
  /** Carbs per prepared portion / bottle. Bike and generic products. */
  carbs?: number | null;
  /** Carbs per 1000 ml. Run products, because the Lauf sheet works in cups. */
  carbsPerLitre?: number | null;
  /** Only the Rad sheet carries kcal, so run-only drinks have null. */
  kcal?: number | null;
}

export interface Line {
  kind: LineKind;
  id: string;
  name: string;
  carbs: number;
  kcal: number;
  count: number;
}

export interface VesselOptions {
  discipline: Discipline;
  bottleMl?: number | undefined;
  cupMl?: number | undefined;
}

export interface CalcInput extends VesselOptions {
  weight: number;
  durMinMin: number;
  durMinMax: number;
  stations?: number | undefined;
  drinks: Line[];
  gels: Line[];
  bars: Line[];
}

export interface Range {
  min: number;
  max: number;
  avg: number;
}

export interface CalcResult {
  ceiling: Range;
  hours: Range;
  minutesAvg: number;
  totalCarbs: number;
  totalKcal: number;
  perHour: Range;
  vsCeilingPct: number;
  fluidPerHour: number;
  vessel: number | null;
  vesselsPerRace: number | null;
  vesselsPerHour: number | null;
  cupsPerStation: number | null;
  itemCount: number;
  drinkCount: number;
  sipUnits: number;
  eatEveryMin: number | null;
  anyEveryMin: number | null;
  lines: Line[];
}

export interface ScheduleEntry {
  minute: number;
  kind: LineKind;
  name: string | null;
  detail: string | null;
  preStart?: boolean;
}

const avg = (a: number, b: number): number => (a + b) / 2;
const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Bike drinks are mixed per bottle (carbs per portion). Run drinks come from
 * aid-station cups, so the sheet works per litre: Lauf Z30/Z31 -> Z45.
 * 106 g/L x 100 ml cup = 10.6 g.
 */
export function drinkCarbs(product: Product | null | undefined, opts: VesselOptions): number {
  if (!product) return 0;
  const vessel = opts.discipline === "run" ? (opts.cupMl ?? 0) || 100 : (opts.bottleMl ?? 0) || 750;
  if (opts.discipline === "run") {
    if (product.carbsPerLitre != null) return round2((product.carbsPerLitre * vessel) / 1000);
    return product.carbs ?? 0;
  }
  if (product.carbs != null) return product.carbs;
  if (product.carbsPerLitre != null) return round2((product.carbsPerLitre * vessel) / 1000);
  return 0;
}

/**
 * Drink lines store only the product id; their carbs are always re-derived from
 * the current vessel so changing the cup or bottle rescales them.
 */
export function withDerivedDrinks(input: CalcInput, catalogue?: Product[] | null): CalcInput {
  if (!catalogue) return input;
  return {
    ...input,
    drinks: input.drinks.map((line) => {
      const product = catalogue.find((candidate) => candidate.id === line.id);
      return { ...line, carbs: drinkCarbs(product, input), kcal: product?.kcal ?? 0 };
    }),
  };
}

/** Drinks a given discipline can actually use. */
export function drinksFor(discipline: Discipline, catalogue: Product[]): Product[] {
  return catalogue.filter((p) =>
    discipline === "run" ? p.carbsPerLitre != null || p.carbs === 0 : p.carbs != null,
  );
}

export function calc(input: CalcInput): CalcResult {
  const { weight, durMinMin, durMinMax, discipline, bottleMl, cupMl, stations } = input;

  // ceiling: weight x 1.0-1.3 g/h                        (Rad AL16 / AM16)
  const ceilingMin = weight * K.kgMin;
  const ceilingMax = weight * K.kgMax;
  const ceiling: Range = { min: ceilingMin, max: ceilingMax, avg: avg(ceilingMin, ceilingMax) };

  // duration in hours                                    (Rad AK10 / AM10 / AN10)
  const hoursMin = durMinMin / 60;
  const hoursMax = durMinMax / 60;
  const hours: Range = { min: hoursMin, max: hoursMax, avg: avg(hoursMin, hoursMax) };
  const minutesAvg = round2(hours.avg * 60); // Rad AO10

  // totals from the chosen products                      (Rad AI32 / AJ32)
  const lines = [...input.drinks, ...input.gels, ...input.bars].filter((l) => l.count > 0);
  const totalCarbs = lines.reduce((sum, l) => sum + l.carbs * l.count, 0);
  const totalKcal = lines.reduce((sum, l) => sum + (l.kcal || 0) * l.count, 0);

  // carbs per hour at the fast and the slow duration      (Rad AK32 / AM32 / AN32)
  const perHourMin = totalCarbs / hours.max;
  const perHourMax = totalCarbs / hours.min;
  const perHour: Range = { min: perHourMin, max: perHourMax, avg: avg(perHourMin, perHourMax) };

  // deviation from the personal ceiling, in %             (Rad A30)
  const vsCeilingPct = (perHour.avg / ceiling.avg) * 100 - 100;

  const fluidPerHour = avg(K.fluidMin, K.fluidMax); // Rad AN13 = 650

  // vessels                                              (Rad F29 / F30, Lauf F31 / H31)
  const isSwim = discipline === "swim";
  const vessel = isSwim ? null : discipline === "run" ? (cupMl ?? 0) || 100 : (bottleMl ?? 0) || 750;
  const vesselsPerRace = vessel === null ? null : (fluidPerHour * hours.avg) / vessel;
  const vesselsPerHour = vessel === null ? null : fluidPerHour / vessel;
  const cupsPerStation =
    discipline === "run" && stations && vesselsPerRace !== null ? vesselsPerRace / stations : null;

  // eating and drinking rhythm                           (Rad C38 / C39)
  const itemCount = [...input.gels, ...input.bars].reduce((sum, l) => sum + l.count, 0);
  const drinkCount = input.drinks.reduce((sum, l) => sum + l.count, 0);
  const sipUnits = isSwim || vessel === null ? 0 : (vessel * drinkCount) / K.mlPerFourSips; // Rad H38 -> AI38
  const eatEveryMin = itemCount ? minutesAvg / (itemCount + 1) : null;
  const anyEveryMin = itemCount + sipUnits > 0 ? minutesAvg / (itemCount + sipUnits) : null;

  return {
    ceiling,
    hours,
    minutesAvg,
    totalCarbs,
    totalKcal,
    perHour,
    vsCeilingPct,
    fluidPerHour,
    vessel,
    vesselsPerRace,
    vesselsPerHour,
    cupsPerStation,
    itemCount,
    drinkCount,
    sipUnits,
    eatEveryMin,
    anyEveryMin,
    lines,
  };
}

/**
 * Turn the intervals into a real schedule: items spread evenly, sips between.
 * Swimming is a special case -- no drinking at all, and its fuel is taken before
 * the gun rather than distributed across the leg.
 */
export function schedule(input: CalcInput, res: CalcResult): ScheduleEntry[] {
  const isSwim = input.discipline === "swim";
  const items: Array<Pick<Line, "kind" | "name" | "carbs" | "kcal">> = [];

  for (const line of [...input.gels, ...input.bars]) {
    // A "Kein Gel" / "Kein Riegel" line is not an event.
    if (!line.carbs && !line.kcal) continue;
    for (let i = 0; i < line.count; i++) {
      items.push({ kind: line.kind, name: line.name, carbs: line.carbs, kcal: line.kcal });
    }
  }
  if (!items.length) return [];

  const detailOf = (it: (typeof items)[number]): string =>
    `${it.carbs} g${it.kcal ? ` · ${it.kcal} kcal` : ""}`;

  if (isSwim) {
    return items.map((it) => ({
      minute: 0,
      preStart: true,
      kind: it.kind,
      name: it.name,
      detail: detailOf(it),
    }));
  }
  if (!res.eatEveryMin) return [];

  const out: ScheduleEntry[] = items.map((it, i) => ({
    minute: Math.round(res.eatEveryMin! * (i + 1)),
    kind: it.kind,
    name: it.name,
    detail: detailOf(it),
  }));

  // Drinking cadence, coarse: one 4-sip mark every anyEveryMin, skipping item slots.
  if (res.anyEveryMin) {
    for (let m = res.anyEveryMin; m < res.minutesAvg; m += res.anyEveryMin * 2) {
      const minute = Math.round(m);
      if (out.some((o) => Math.abs(o.minute - minute) < 2)) continue;
      out.push({ minute, kind: "drink", name: null, detail: null });
    }
  }
  return out.sort((a, b) => a.minute - b.minute);
}

/** `2:30` -- elapsed time, colon-separated, per the house voice. */
export function fmtTime(minutes: number): string {
  return `${Math.floor(minutes / 60)}:${String(Math.round(minutes % 60)).padStart(2, "0")}`;
}

export interface LegResult {
  res: CalcResult;
}

/** Roll several legs into one plan (the Zusammenfassung sheet). */
export function total(legs: LegResult[]): {
  carbs: number;
  kcal: number;
  minutes: number;
  items: number;
  drinks: number;
  perHour: number;
} {
  const carbs = legs.reduce((s, l) => s + l.res.totalCarbs, 0);
  const kcal = legs.reduce((s, l) => s + l.res.totalKcal, 0);
  const minutes = legs.reduce((s, l) => s + l.res.minutesAvg, 0);
  const items = legs.reduce((s, l) => s + l.res.itemCount, 0);
  const drinks = legs.reduce((s, l) => s + l.res.drinkCount, 0);
  return { carbs, kcal, minutes, items, drinks, perHour: minutes ? carbs / (minutes / 60) : 0 };
}
